import { createSlice } from '@reduxjs/toolkit';
// utils
import moment from 'moment';
import { getParsedData, localStorageSetItem } from 'src/utils/storage-available';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { callApi } from '../../utils/apiCall';
import { reminderFields } from '../../constants/fields';
import { isOldRemindersDisabled } from '../../utils/oldRemindersDisabled';
//
import { dispatch, store } from '../store';
import { updateError } from './user';
import { getPracticeId } from './clincheck';

// ---------------------------------------------------------------------
const initialState = {
    isLoading: false,
    toDoNotificationData: [],
    reminderNotificationsData: [],
    toDoData: [],
    reminderData: [],
    authFailed: false,
};

const slice = createSlice({
    name: 'toDoNotifications',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        setToDoNotificationData(state, action) {
            state.toDoNotificationData = action.payload;
        },
        setReminderNotificationsData(state, action) {
            state.reminderNotificationsData = action.payload;
        },
        setToDoData(state, action) {
            state.toDoData = action.payload;
        },
        setReminderData(state, action) {
            state.reminderData = action.payload;
        },
        setAuthFailed(state) {
            state.authFailed = true;
        },
        clearAuthFailed(state) {
            state.authFailed = false;
        },
    },
});

// Reducer
export default slice.reducer;

export const { setToDoNotificationData, setReminderNotificationsData, reset, setToDoData, setReminderData, setAuthFailed, clearAuthFailed } = slice.actions;

export const getMessage = (firstName, lastName, treatment, method) => {
    const messageForFutureDate = `${method ?? "Contact"} ${firstName ?? ""} ${lastName ?? ""} about ${treatment ?? "Treatment"}`;
    return messageForFutureDate;
};

export const handleSnooze = (data, handleCloseNotiFications) => {
    const { id, firstName, treatment, lastName, method, snoozeTime } = data;
    const currentTime = moment();
    const snoozeEndTime = moment(snoozeTime);
    if (currentTime.isSameOrAfter(snoozeEndTime)) {
        const { toDoData, reminderData } = store.getState().toDoNotifications;
        const temp = {
            ...data,
            message: getMessage(firstName, lastName, treatment, method)
        };
        const uniqReminderData = reminderData.filter((ele) => ele.id !== id);
        const uniqToDoData = toDoData.filter((ele) => ele.id !== id);
        localStorageSetItem('reminderData', JSON.stringify([temp, ...uniqReminderData]));
        dispatch(setToDoData([temp, ...uniqToDoData]?.slice(0, 2)));
        dispatch(setReminderData([temp, ...uniqReminderData]));
    } else {
        const timeout = setTimeout(() => {
            const { toDoData, reminderData } = store.getState().toDoNotifications;
            const temp = {
                ...data,
                message: getMessage(firstName, lastName, treatment, method)
            };
            const uniqReminderData = reminderData.filter((ele) => ele.id !== id);
            const uniqToDoData = toDoData.filter((ele) => ele.id !== id);
            localStorageSetItem('reminderData', JSON.stringify([temp, ...uniqReminderData]));
            dispatch(setToDoData([temp, ...uniqToDoData]?.slice(0, 2)));
            dispatch(setReminderData([temp, ...uniqReminderData]));
        }, snoozeEndTime.diff(currentTime));
        const oldTimeOutIds = getParsedData('timeout_ids') || [];
        localStorageSetItem('timeout_ids', JSON.stringify([...oldTimeOutIds, timeout]));
    }
    handleCloseNotiFications(id);
};

export const getTime = (dateTime, isWp) => {
    const dateTimeString = moment(dateTime, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ');
    if (isWp) return `${moment(dateTimeString, 'HH:mm').format('hh:mm A')} `;
    return `Due today at ${moment(dateTimeString, 'HH:mm').format('hh:mm A')} `;
};

function showSystemNotification(title, body) {
    try {
        // Check for notification permission first
        if (Notification.permission === "granted") {
            new Notification(title, {
                body,
                icon: "/favicon/android-chrome-512x512.png" // <- Provide a valid path
            });
        } else if (Notification.permission === "default") {
            // If it's not denied, we can ask for permission
            Notification.requestPermission().then(permission => {
                if (permission === "granted") {
                    new Notification(title, {
                        body,
                        icon: "/favicon/android-chrome-512x512.png"
                    });
                }
            });
        }
    } catch (error) {
        console.error("Error in OS Notif", error)
    }
}

export function getToDoNotificationsData(data) {
    return async () => {
        // Guard: skip API call if practiceId is missing or no auth token
        const practiceId = getPracticeId();
        if (!practiceId || !localStorage.getItem('accessToken')) return;
        try {
            const fields = `&fields=${reminderFields}`;
            // All Practices phase-2 consumer: aggregate the dashboard
            // right-rail reminders across every practice in the current
            // selection so task counts match the lead-table / reminders-
            // sidebar rows. Suffix is '' for single-practice users.
            const allPracticesSuffix = practiceInSuffix(
                store.getState().practiceTreatmentList?.activePracticeIds,
            );
            const response = await callApi(
                `/api/v1/practices/${practiceId}/reminders/?datetime__gte=${data.startDate}&datetime__lte=${data.endDate
                }${fields}&is_completed=false&lead__is_archived=false${allPracticesSuffix}`
            );
            if (response?.status === 200) {
                dispatch(slice.actions.clearAuthFailed());
                const newNotifications = response?.data?.results?.filter((ele) => ele?.lead?.id)?.map((ele, index) => ({
                    leadId: ele.lead.id,
                    id: ele.id,
                    message: getMessage(
                        ele?.lead?.first_name,
                        ele?.lead?.last_name,
                        ele?.lead_treatment?.treatment,
                        ele?.method,
                    ),
                    dateTime: ele?.datetime,
                    firstName: ele?.lead?.first_name,
                    HumanReadable: ele.datetime_human,
                    lastName: ele?.lead?.last_name,
                    method: ele?.method,
                    treatment: ele?.lead_treatment?.treatment,
                    key: index
                }));
                if (document.visibilityState === 'visible')
                    response?.data?.results?.forEach(notif => {
                        showSystemNotification('DentalSem Reminder!', getMessage(
                            notif?.lead?.first_name,
                            notif?.lead?.last_name,
                            notif?.lead_treatment?.treatment,
                            notif?.method,
                        ) || 'You have a new notification');
                    })
                localStorageSetItem('reminderData', JSON.stringify(newNotifications));
                dispatch(slice.actions.setToDoData(newNotifications?.slice(0, 2)));
                dispatch(slice.actions.setReminderData(newNotifications));
            } else if (!response?.status) {
                // Status 0 = no response at all (offline, laptop waking,
                // request timed out). This is a background poll the user never
                // asked for and it runs again in 5 minutes, so it must not pop
                // a "Something went wrong" the user can do nothing about
                // (JAVASCRIPT-REACT-10R). Real HTTP errors still toast below.
            } else if (!isOldRemindersDisabled(response)) {
                dispatch(updateError({ success: false, message: response?.data || 'Something went wrong', status: response?.status }));
            }
        } catch (error) {
            console.error("Erron in fetching reminders", error);
            if (error?.response?.status === 401 || error?.status === 401) {
                dispatch(slice.actions.setAuthFailed());
            } else {
                dispatch(updateError({ success: false, message: 'Something went wrong', cause: error instanceof Error ? new Error(error.message) : new Error(String(error?.message ?? 'error')) }));
            }
        }
    };
}